import axios from 'axios';
import { decodeJwt } from 'jose';
import { getIsRefreshing, setIsRefreshing } from './axiosInterceptor';

/**
 * Proactive token refresh service
 * Refreshes access tokens before they expire to prevent 401 errors
 */
class TokenRefreshService {
  constructor() {
    this.refreshTimer = null;
    this.refreshBuffer = 5000; // 5 seconds buffer before expiry
    this.refreshPromise = null;
    this.heartbeatInterval = null;
  }

  /**
   * Get token expiration time in milliseconds
   */
  getTokenExpirationTime(token) {
    if (!token) return 0;
    try {
      const payload = decodeJwt(token);
      const expTime = payload.exp * 1000;
      return expTime;
    } catch {
      return 0;
    }
  }

  /**
   * Check if token is expired or will expire soon
   */
  isTokenExpiringSoon(token, bufferMs = this.refreshBuffer) {
    const expirationTime = this.getTokenExpirationTime(token);
    const now = Date.now();
    return expirationTime - now <= bufferMs;
  }

  /**
   * Refresh the access token
   */
  async refreshToken() {
    // If already refreshing (shared flag with axios interceptor), return the existing promise
    if (getIsRefreshing() && this.refreshPromise) {
      return this.refreshPromise;
    }

    setIsRefreshing(true);
    this.refreshPromise = this.performRefresh();

    try {
      const response = await this.refreshPromise;
      return response;
    } finally {
      setIsRefreshing(false);
      this.refreshPromise = null;
    }
  }

  /**
   * Perform the actual refresh request
   */
  async performRefresh() {
    try {
      const response = await axios.post(
        '/auth/refresh',
        {},
        {
          withCredentials: true, // Ensure cookies are sent
        },
      );

      if (response.data?.data?.accessToken) {
        const newToken = response.data.data.accessToken;
        return newToken;
      } else {
        throw new Error('Invalid refresh response');
      }
    } catch (error) {
      // If refresh fails, the user needs to re-authenticate
      // Clear any existing timers
      this.stopRefreshTimer();

      // Throw error so caller can handle logout
      throw error;
    }
  }

  /**
   * Start the proactive refresh timer
   */
  startRefreshTimer(accessToken, onTokenRefreshed, onRefreshFailed) {
    // Clear any existing timer
    this.stopRefreshTimer();

    if (!accessToken) {
      return;
    }

    const expirationTime = this.getTokenExpirationTime(accessToken);
    const now = Date.now();
    const timeUntilExpiry = expirationTime - now;
    const timeUntilRefresh = Math.max(0, timeUntilExpiry - this.refreshBuffer);

    // Set timer to refresh token
    this.refreshTimer = setTimeout(async () => {
      try {
        const newToken = await this.refreshToken();

        // Callback with new token
        if (onTokenRefreshed) {
          onTokenRefreshed(newToken);
        }

        // Start timer again with new token
        this.startRefreshTimer(newToken, onTokenRefreshed, onRefreshFailed);
      } catch (error) {
        // Callback with error
        if (onRefreshFailed) {
          onRefreshFailed(error);
        }
      }
    }, timeUntilRefresh);

    // Start heartbeat to check token validity periodically
    this.startHeartbeat(accessToken, onTokenRefreshed, onRefreshFailed);
  }

  /**
   * Start heartbeat to periodically check token status
   */
  startHeartbeat(accessToken, onTokenRefreshed, onRefreshFailed) {
    // Clear existing heartbeat
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    // Check every 10 seconds (adjust as needed)
    this.heartbeatInterval = setInterval(() => {
      // Don't trigger logout on expired tokens - let the proactive refresh timer handle it
      // The heartbeat is only for detecting tokens that are expiring soon, not already expired
      if (this.isTokenExpiringSoon(accessToken, this.refreshBuffer * 2)) {
        this.startRefreshTimer(accessToken, onTokenRefreshed, onRefreshFailed);
      }
    }, 10000);
  }

  /**
   * Stop the refresh timer and heartbeat
   */
  stopRefreshTimer() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Restart refresh timer with new token
   */
  restartWithNewToken(newAccessToken, onTokenRefreshed, onRefreshFailed) {
    this.stopRefreshTimer();
    this.startRefreshTimer(newAccessToken, onTokenRefreshed, onRefreshFailed);
  }

  /**
   * Check if service is currently active
   */
  isTimerActive() {
    return this.refreshTimer !== null || this.heartbeatInterval !== null;
  }

  /**
   * Get time until token expires in seconds
   */
  getTimeUntilExpiry(accessToken) {
    const expirationTime = this.getTokenExpirationTime(accessToken);
    const now = Date.now();
    return Math.max(0, (expirationTime - now) / 1000);
  }
}

// Create singleton instance
const tokenRefreshService = new TokenRefreshService();

export default tokenRefreshService;
